/* eslint max-lines: 0 */
import TextInput from 'components/Input/TextInput';
import Modal from 'components/Modal';
import { Formik, Form, FormikContextType, useFormikContext } from 'formik';
import * as Yup from 'yup';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import actions from 'state/actions';
import services from 'services';
import utils from 'utils';
import styled from 'styled-components/macro';
import Dropdown from 'components/Input/dropdown/Dropdown';
import CommonButton from 'components/Buttons/CommonButton';
import { useSelector } from 'react-redux';
import selectors from 'state/selectors';
import { DocumentHeaderResponse, ISubjectPrediction, UserCompany } from 'models/response';
import Textarea from 'components/Input/Textarea';
import { BulkInsertBody } from 'models/request';
import { useAsync } from 'react-use';
import { FormValues } from 'models/documents';
import { FilePosition, FilePositionTypes } from '../../DocumentSearch/index.types';
import { slice } from 'state/documents/slice';
import { applyRemoveField, useExecuteToolbarActionDocHeader } from 'utils/helpers/toolbar.helpers';

const ModalContainer = styled.div`
  min-width: 550px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 15px;
`;

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  > * {
    margin-bottom: 30px;
  }
`;

const FormContainer = styled.div`
  display: flex;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 8px;
`;

const InputLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
  margin-right: 15px;
`;

const Left = styled.div`
  margin-right: 30px;
`;

const Right = styled.div``;

const NewStore = () => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();
  const companies = useSelector(selectors.app.getCompanies);
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const activeDocument = useSelector(selectors.documents.selectActiveDocument);
  const inputFile = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMultiple, setIsMultiple] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [multiErrors, setMultiErrors] = useState<any[]>([]);
  const [initialFormValues, setInitialFormValues] = useState<ISubjectPrediction>({
    company: null,
    subject: '',
    vatNumber: '',
    address: '',
    country: null,
    fiscalCode: '',
  });
  const [countryList, setCountryList] = useState<{ code: string; description: string }[]>([]);
  const isOpenDocumentView = utils.documents.isOpenDocumentView();
  const saveAction = utils.hooks.useSaveAction();
  const selectedDocuments = useSelector(selectors.documents.selectAllActiveDocuments);
  const tableRows = useSelector(selectors.configurator.selectTableRows);
  const idTemplate = useSelector(selectors.documents.selectActiveTemplateId);
  const isArchived = useSelector(selectors.documents.selectIsTemplateArchived);
  const idProgram = useSelector(selectors.app.getProgramCode);
  const protocols = selectedDocuments.map(({ protocol }) => protocol);
  const documentHeader = useSelector(selectors.documents.selectDocumentHeader);
  const documentFields = useSelector(selectors.documents.selectDocumentFields);

  const { values } = useFormikContext<FormValues>();
  const resetManualDirty = utils.hooks.useResetManualDirty();
  const executeToolbarAction = useExecuteToolbarActionDocHeader();

  useAsync(async () => {
    if (protocol && activeDocument) {
      try {
        const response = await services.getSubjectPrediction(protocol);
        const { company, fiscalCode, subject, vatNumber, address, country } = response.data;

        if (company) {
          const { data } = await services.getAP2HTable('h_country', company?.value);
          setCountryList(data);
        }

        setInitialFormValues({
          company,
          subject,
          vatNumber,
          address,
          country,
          fiscalCode,
        });
      } catch (error) {
        console.error(error);
      }
    }
  });

  const getSample = async () => {
    try {
      const { data } = await services.getCsvTemplateForCreateSubjectByFile();
      utils.file.downloadFileFromBase64Source('sample', data, 'text/csv', 'csv');
    } catch (err) {
      console.error(err);
    }
  };

  const companyOptions = (data: UserCompany[]) => {
    const values = utils.input.removeDefaultValue(data, 'idCompany');
    const options = utils.input.buildOptions(values, 'name', 'idCompany');

    return options;
  };

  const validationLogic = Yup.object({
    company: Yup.object()
      .shape({
        label: Yup.string().required(t('required')),
        value: Yup.number().required(t('required')),
      })
      .required(t('required')),
    subject: Yup.string().required(t('required')),
    vatNumber: Yup.string().required(t('required')),
    country: Yup.object()
      .shape({
        label: Yup.string().required(t('required')),
        value: Yup.string().required(t('required')),
      })
      .nullable()
      .required(t('required')),
  });

  const isBulkInsertBody = (data: {
    idCompany: number | null;
    base64Csv: string | Error | ArrayBuffer | null;
  }): data is BulkInsertBody => {
    return data.idCompany !== null && typeof data.base64Csv === 'string' && data.base64Csv !== '';
  };

  const setDocumentHeader = async (): Promise<DocumentHeaderResponse | undefined> => {
    const protocol = protocols[0];
    const filePosition = tableRows.find((el) => el.protocol === protocol)?.position as FilePosition;
    if (!idTemplate) return;
    try {
      const { data } =
        isArchived || filePosition === FilePositionTypes.archived
          ? await services.getArchivedDocumentHeader({ protocol, idProgram, idTemplate })
          : await services.getDocumentHeader({ protocol, idProgram, idTemplate });
      dispatch(slice.actions.setDocumentHeader(data));
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  const reloadPage = async (idSubject: number, subject: string, supplierCode: string) => {
    const previousSupplierCode = values.supplierCode;
    if (protocols) {
      const data = await setDocumentHeader();
      dispatch(actions.documents.setDocumentFields(protocols[0], idProgram));
      dispatch(
        actions.documents.updateTableRows({
          protocols,
          property: 'idSubject',
          value: idSubject,
        }),
      );
      dispatch(
        actions.documents.updateTableRows({
          protocols,
          property: 'subject',
          value: subject,
        }),
      );

      dispatch(
        actions.documents.updateTableRows({
          protocols,
          property: 'supplierCode',
          value: supplierCode,
        }),
      );

      if (data?.numberOfDetails === 1 && previousSupplierCode !== null) executeToolbarAction('supplierCode');
    }
  };

  const setSubject = async (val: number, subject: string, vatNumber: string) => {
    try {
      if (protocols) {
        const updatedValues = applyRemoveField(
          documentHeader?.triggerConfig ?? [],
          'supplierCode',
          values,
          documentFields,
        );

        await saveAction(updatedValues);
        resetManualDirty(updatedValues);

        await services.setSubject({
          idSubject: val,
          protocols,
        });
        dispatch(actions.modal.closeModal());
        reloadPage(val, subject, vatNumber);
        utils.app.notify('success', t('success'));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSingleInsert = async (values: ISubjectPrediction) => {
    try {
      const { data } = await services.insertSubject({
        subject: values.subject,
        vatNumber: values.vatNumber,
        address: values.address,
        country: values.country?.value ?? null,
        fiscalCode: values.fiscalCode,
        idCompany: values.company?.value ?? null,
        debitCredit: 0,
        validationType: 0,
        blocked: 0,
      });

      utils.app.notify('success', t('subject_added'));
      setSubject(data, values.subject, values.vatNumber);
      dispatch(actions.modal.closeModal());
    } catch (err) {
      console.error(err);
      utils.app.notify('fail', t('insert-error'));
    }
  };

  const handleBulkInsert = async (values: ISubjectPrediction) => {
    if (!csvFile) return;
    if (!values.company) {
      utils.app.notify('fail', t('company-required'));
      return;
    }

    try {
      const base = await utils.file.toBase64(csvFile);
      const apiData = { idCompany: values.company.value, base64Csv: base };

      if (!isBulkInsertBody(apiData)) {
        utils.app.notify('fail', t('data-error'));
        return;
      }

      const { data } = await services.bulkInsert(apiData);

      if (data.rowsInError.length === 0) {
        utils.app.notify('success', t('upload-success'));
      } else {
        setMultiErrors(data.rowsInError);
        utils.app.notify('fail', t('upload-failure'));
      }
    } catch (err) {
      console.error(err);
      utils.app.notify('fail', t('upload-failure'));
    }
  };

  const handleSubmit = async (values: ISubjectPrediction) => {
    if (isMultiple) {
      await handleBulkInsert(values);
    } else {
      await handleSingleInsert(values);
    }
  };

  const initialTouched = {
    subject: false,
    vatNumber: false,
    address: false,
    fiscalCode: false,
    country: false,
  };
  const handleClose = () => {
    dispatch(actions.modal.closeModal());
  };

  const formikRef = useRef<FormikContextType<ISubjectPrediction>>(null);
  return (
    <>
      <Modal.Header title={t('Add-new-store')} subtitle={t('store-subtitle')} />
      <Modal.Content>
        {/* <ToggleContainer>
          <Toggle onChange={() => setIsMultiple(!isMultiple)} />
          <ToggleText>{t('multiple-entry')}</ToggleText>
        </ToggleContainer> */}
        <Formik
          // @ts-ignore
          innerRef={formikRef}
          initialValues={initialFormValues}
          validationSchema={!isMultiple && validationLogic}
          onSubmit={handleSubmit}
          initialTouched={initialTouched}
          enableReinitialize={true}
        >
          {({ values, errors, handleChange, setFieldValue, submitForm, touched, handleBlur }) => {
            return (
              <Form>
                <ModalContainer>
                  {!isMultiple ? (
                    <>
                      <FormContainer>
                        <Left>
                          <InputContainer style={{ alignItems: 'baseline' }}>
                            <InputLabel>{t('company')}</InputLabel>
                            <Dropdown
                              id="company-dropdown"
                              name="company"
                              hasFeedback={errors.company ? true : false}
                              feedbackMessage={errors.company ? t(`${errors.company}`) : ''}
                              value={values.company}
                              onChange={(option) => setFieldValue('company', option)}
                              options={companyOptions(companies)}
                              disabled={utils.documents.isOpenDocumentView()}
                              width="200px"
                            />
                          </InputContainer>
                          <InputContainer style={{ alignItems: 'baseline' }}>
                            <InputLabel>{t('assetId')}</InputLabel>
                            <TextInput
                              id="new-store-subject"
                              hasFeedback={touched.subject && errors.subject ? true : false}
                              feedbackMessage={errors.subject ? t(`${errors.subject}`) : ''}
                              name="subject"
                              value={values.subject}
                              onChange={handleChange}
                              displayText="block"
                              textPositioning="relative"
                              onBlur={handleBlur}
                              width="200px"
                            />
                          </InputContainer>
                          <InputContainer style={{ alignItems: 'baseline' }}>
                            <InputLabel>{t('assetName')}</InputLabel>
                            <TextInput
                              id="new-store-vat-number"
                              hasFeedback={touched.vatNumber && errors.vatNumber ? true : false}
                              feedbackMessage={errors.vatNumber ? errors.vatNumber : ''}
                              name="vatNumber"
                              value={values.vatNumber}
                              onChange={handleChange}
                              displayText="block"
                              textPositioning="relative"
                              onBlur={handleBlur}
                              width="200px"
                            />
                          </InputContainer>
                        </Left>
                        <Right>
                          <InputContainer style={{ alignItems: 'baseline' }}>
                            <InputLabel>{t('MallName')}</InputLabel>
                            <TextInput
                              id="new-store-fiscal-code"
                              hasFeedback={touched.fiscalCode && errors.fiscalCode ? true : false}
                              feedbackMessage={errors.fiscalCode ? t(`${errors.fiscalCode}`) : ''}
                              name="fiscalCode"
                              value={values.fiscalCode}
                              onChange={handleChange}
                              displayText="block"
                              textPositioning="relative"
                              onBlur={handleBlur}
                              width="200px"
                            />
                          </InputContainer>
                          <InputContainer style={{ alignItems: 'baseline' }}>
                            <InputLabel>{t('country')}</InputLabel>
                            <Dropdown
                              id="country-dropdown"
                              name="country"
                              hasFeedback={errors.country ? true : false}
                              feedbackMessage={errors.country ? t(`${errors.country}`) : ''}
                              value={values.country}
                              onChange={(option) => setFieldValue('country', option)}
                              options={utils.input.buildOptions(countryList, 'description', 'code')}
                              width="200px"
                            />
                          </InputContainer>
                          <InputContainer style={{ alignItems: 'baseline' }}>
                            <InputLabel>{t('address')}</InputLabel>
                            <TextInput
                              id="new-store-address"
                              hasFeedback={touched.address && errors.address ? true : false}
                              feedbackMessage={errors.address ? t(`${errors.address}`) : ''}
                              name="address"
                              value={values.address}
                              onChange={handleChange}
                              displayText="block"
                              textPositioning="relative"
                              onBlur={handleBlur}
                              width="200px"
                            />
                          </InputContainer>
                        </Right>
                      </FormContainer>
                      <ButtonContainer>
                        <CommonButton
                          id="submit-btn"
                          action={() => {
                            submitForm();
                            handleClose();
                          }}
                          type="button"
                          scope="primary"
                          value={t('submit')}
                          disabled={Object.keys(errors).length !== 0}
                        />
                        <CommonButton
                          id="cancel-btn"
                          action={() => handleClose()}
                          type="button"
                          scope="secondary"
                          value={'cancel'}
                        />
                      </ButtonContainer>
                    </>
                  ) : (
                    <UploadContainer>
                      <InputContainer>
                        <InputLabel>{t('company')}</InputLabel>
                        <Dropdown
                          id="company-dropdown"
                          name="company"
                          hasFeedback={errors.company ? true : false}
                          feedbackMessage={errors.company ? t(`${errors.company}`) : ''}
                          value={values.company}
                          onChange={(option) => setFieldValue('company', option)}
                          options={companyOptions(companies)}
                          disabled={isOpenDocumentView}
                        />
                      </InputContainer>
                      <CommonButton
                        id="download-sample-btn"
                        action={() => getSample()}
                        type="button"
                        scope="secondary"
                        value={t('download-formatted-sample')}
                      />
                      <>
                        <input
                          ref={inputFile}
                          style={{ display: 'none' }}
                          accept={'.csv'}
                          type="file"
                          onChange={(e: any) => setCsvFile(e.target.files[0])}
                        />
                        <CommonButton
                          id="upload-csv-btn"
                          action={() => inputFile?.current?.click()}
                          type="button"
                          scope="primary"
                          value={t('upload-csv')}
                        />
                      </>
                      {multiErrors.length > 0 && (
                        <>
                          <h3>{t('Upload errors')}</h3>
                          <Textarea
                            value={multiErrors.map(
                              (el) => `Row number: ${el.rowNumber}\nError Message: ${el.errorMessage} \n`,
                            )}
                          ></Textarea>
                        </>
                      )}
                    </UploadContainer>
                  )}
                </ModalContainer>
              </Form>
            );
          }}
        </Formik>
      </Modal.Content>
      <Modal.Footer
        withConfirmButton={isMultiple}
        confirmAction={async () => {
          try {
            await formikRef.current?.submitForm();
            utils.app.notify('success', t('multiple-stores-uploaded-successfully'));
          } catch (e) {
            console.error(e);
            utils.app.notify('fail', t('error-while-uploading-stores'));
          }
        }}
        withCloseButton={isMultiple}
        confirmDisabled={isMultiple && (!formikRef.current?.values.company?.value || csvFile === null)}
        confirmText={isMultiple ? t('submit-csv') : t('submit')}
      />
    </>
  );
};

export default NewStore;
